import {
  Download,
  Loader2,
  Upload,
  X,
  Lock,
  Sparkles,
  Check,
  ArrowRight,
} from "lucide-react";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { z } from "zod";

import employeeService from "../../services/employee";
import { Button } from "../ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Input } from "../ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { toast } from "../ui/use-toast";

interface BulkUploadRow {
  id: string;
  invitationType: "email" | "sms";
  email?: string;
  phone?: string;
  fullName: string;
  errors?: string[];
}

interface BulkUploadTabProps {
  hasAccess: boolean;
}

const BulkUploadTab: React.FC<BulkUploadTabProps> = ({ hasAccess }) => {
  const [csvData, setCsvData] = useState<BulkUploadRow[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const downloadExampleCSV = () => {
    const exampleData = [
      ["invitationType", "email", "phone", "fullName"],
      ["email", "john.doe@example.com", "", "John Doe"],
      ["sms", "", "+1234567890", "Jane Smith"],
      ["email", "bob.wilson@example.com", "", "Bob Wilson"],
    ];

    const csvContent = exampleData.map((row) => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "bulk_upload_example.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseCSV = (csvText: string): BulkUploadRow[] => {
    const lines = csvText.split("\n").filter((line) => line.trim());
    if (lines.length < 2) {
      throw new Error("CSV file must have at least a header row and one data row");
    }

    const headers = lines[0]
      .split(",")
      .map((h) => h.trim().toLowerCase())
      .filter((h) => h);

    // Validate headers
    const hasInvitationType = headers.includes("invitationtype");
    const hasFullName = headers.includes("fullname");
    const hasEmail = headers.includes("email");
    const hasPhone = headers.includes("phone");

    if (!hasInvitationType || !hasFullName) {
      throw new Error(
        "CSV must contain 'invitationType' and 'fullName' columns"
      );
    }

    if (!hasEmail && !hasPhone) {
      throw new Error("CSV must contain either 'email' or 'phone' column");
    }

    const rows: BulkUploadRow[] = [];
    const errors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(",").map((v) => v.trim());
      const row: any = {};

      headers.forEach((header, index) => {
        row[header] = values[index] || "";
      });

      try {
        const invitationType = (
          row.invitationtype || ""
        ).toLowerCase() as "email" | "sms";

        if (!["email", "sms"].includes(invitationType)) {
          errors.push(
            `Row ${i + 1}: invitationType must be 'email' or 'sms'`
          );
          continue;
        }

        const email = row.email || "";
        const phone = row.phone || "";
        const fullName = row.fullname || "";

        // Validate based on invitation type
        if (invitationType === "email" && !email) {
          errors.push(`Row ${i + 1}: email is required when invitationType is 'email'`);
          continue;
        }

        if (invitationType === "sms" && !phone) {
          errors.push(`Row ${i + 1}: phone is required when invitationType is 'sms'`);
          continue;
        }

        // Validate email format if provided
        if (email && !z.string().email().safeParse(email).success) {
          errors.push(`Row ${i + 1}: invalid email format`);
          continue;
        }

        rows.push({
          id: `row-${i}`,
          invitationType,
          email: invitationType === "email" ? email : undefined,
          phone: invitationType === "sms" ? phone : undefined,
          fullName,
        });
      } catch (error: any) {
        errors.push(`Row ${i + 1}: ${error.message}`);
      }
    }

    if (errors.length > 0) {
      throw new Error(`Validation errors:\n${errors.join("\n")}`);
    }

    return rows;
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast({
        title: "Invalid File Type",
        description: "Please upload a CSV file",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const parsedData = parseCSV(csvText);
        setCsvData(parsedData);
        toast({
          title: "File Uploaded Successfully",
          description: `Parsed ${parsedData.length} employee(s) from CSV file`,
        });
      } catch (error: any) {
        toast({
          title: "CSV Parsing Error",
          description: error.message,
          variant: "destructive",
        });
        setCsvData([]);
      } finally {
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      toast({
        title: "File Read Error",
        description: "Failed to read the CSV file",
        variant: "destructive",
      });
      setIsProcessing(false);
    };

    reader.readAsText(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "text/csv": [".csv"],
    },
    multiple: false,
  });

  const updateRow = (id: string, field: keyof BulkUploadRow, value: any) => {
    setCsvData((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const removeRow = (id: string) => {
    setCsvData((prev) => prev.filter((row) => row.id !== id));
  };

  const handleBulkInvite = async () => {
    if (csvData.length === 0) {
      toast({
        title: "No Data",
        description: "Please upload a CSV file with employee data",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    try {
      // Prepare bulk invite data
      const bulkData = csvData.map((row) => ({
        fullName: row.fullName,
        email: row.email,
        phone: row.phone,
        inviteChannelPref: row.invitationType,
        source: "bulk" as const,
      }));

      // Call bulk invite API
      const response = await employeeService.sendBulkInvitation(bulkData);

      if (response.failed === 0) {
        toast({
          title: "All Invitations Sent!",
          description: `Successfully sent ${response.success} invitation(s)`,
        });
      } else if (response.success > 0) {
        toast({
          title: "Partial Success",
          description: `Sent ${response.success} invitation(s), ${response.failed} failed.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Bulk Invite Failed",
          description: "Failed to send invitations. Please try again.",
          variant: "destructive",
        });
      }
      setCsvData([]);
    } catch (error: any) {
      toast({
        title: "Bulk Invite Failed",
        description: error.message || "An error occurred while sending invitations",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Download Example Button */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Bulk Upload Employees</h3>
          <p className="text-sm text-muted-foreground">
            Upload a CSV file to invite multiple employees at once
          </p>
        </div>
        {hasAccess && (
          <Button variant="outline" onClick={downloadExampleCSV}>
            <Download className="mr-2 h-4 w-4" />
            Download Example CSV
          </Button>
        )}
      </div>

      {/* File Upload Area */}
      {csvData.length === 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="relative">
              {!hasAccess && (
                <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
                  <div className="text-center space-y-4 p-8">
                    <div className="flex justify-center">
                      <div className="p-4 rounded-full bg-primary/10">
                        <Lock className="h-8 w-8 text-primary" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold mb-2">
                        Upgrade Your Plan
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Bulk upload is available in Growth and Enterprise plans
                      </p>
                      <Button onClick={() => window.location.href = "/billing"}>
                        View Plans
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              <div
                {...getRootProps()}
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                  hasAccess
                    ? isDragActive
                      ? "border-primary bg-primary/5 cursor-pointer"
                      : "border-muted-foreground/25 hover:border-primary/50 cursor-pointer"
                    : "border-muted-foreground/25 cursor-not-allowed opacity-50"
                }`}
                style={{ pointerEvents: hasAccess ? "auto" : "none" }}
              >
                <input {...getInputProps()} disabled={!hasAccess} />
                {isProcessing ? (
                  <div className="space-y-4">
                    <Loader2 className="h-12 w-12 mx-auto animate-spin text-primary" />
                    <p className="text-muted-foreground">Processing CSV file...</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Upload className="h-12 w-12 mx-auto text-muted-foreground" />
                    <div>
                      <p className="text-lg font-medium">
                        {isDragActive
                          ? "Drop the CSV file here"
                          : "Drag & drop a CSV file here"}
                      </p>
                      <p className="text-sm text-muted-foreground mt-2">
                        or click to browse files
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      CSV file must contain: invitationType, email/phone, fullName
                    </p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feature Description Card - Only show when no access */}
      {!hasAccess && csvData.length === 0 && (
        <Card className="border-primary/20">
          <CardHeader>
            <CardTitle className="text-lg flex items-center space-x-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <span>Why Bulk Upload?</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium">Save Time</h4>
                  <p className="text-sm text-muted-foreground">
                    Upload multiple employees at once instead of adding them one by one
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium">CSV Import</h4>
                  <p className="text-sm text-muted-foreground">
                    Import employee data from your existing spreadsheets
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Check className="h-5 w-5 text-green-600 mt-0.5" />
                <div>
                  <h4 className="font-medium">Batch Processing</h4>
                  <p className="text-sm text-muted-foreground">
                    Process and send invitations to multiple employees simultaneously
                  </p>
                </div>
              </div>
              <div className="pt-4 border-t">
                <Button
                  onClick={() => window.location.href = "/billing"}
                  className="w-full"
                >
                  Upgrade to Growth or Enterprise
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Table - Only show if has access */}
      {csvData.length > 0 && hasAccess && (
        <Card>
          <CardHeader>
            <CardTitle>Review and Edit Employee Data</CardTitle>
            <CardDescription>
              Review the uploaded data and make any necessary changes before
              sending invitations
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invitation Type</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Full Name</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {csvData.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          <select
                            value={row.invitationType}
                            onChange={(e) =>
                              updateRow(
                                row.id,
                                "invitationType",
                                e.target.value as "email" | "sms"
                              )
                            }
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          >
                            <option value="email">Email</option>
                            <option value="sms">SMS</option>
                          </select>
                        </TableCell>
                        <TableCell>
                          {row.invitationType === "email" ? (
                            <Input
                              value={row.email || ""}
                              onChange={(e) =>
                                updateRow(row.id, "email", e.target.value)
                              }
                              placeholder="email@example.com"
                              type="email"
                            />
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {row.invitationType === "sms" ? (
                            <Input
                              value={row.phone || ""}
                              onChange={(e) =>
                                updateRow(row.id, "phone", e.target.value)
                              }
                              placeholder="+1234567890"
                              type="tel"
                            />
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Input
                            value={row.fullName}
                            onChange={(e) =>
                              updateRow(row.id, "fullName", e.target.value)
                            }
                            placeholder="Full Name"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRow(row.id)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  {csvData.length} employee(s) ready to invite
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setCsvData([])}
                    disabled={isUploading}
                  >
                    Clear All
                  </Button>
                  <Button onClick={handleBulkInvite} disabled={isUploading}>
                    {isUploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending Invitations...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Send {csvData.length} Invitation(s)
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BulkUploadTab;

